import { AppCustomEvent } from '@types'
import get from 'lodash/get'
import set from 'lodash/set'

type CacheEntry<T = unknown> = {
  data: T
  lastUpdated: number
}

export const createServiceCache = () => {
  let cacheStore: Record<string, CacheEntry<unknown>> = {}

  // SSR-safe: only add event listeners on the client
  if (typeof window !== 'undefined') {
    window.addEventListener(AppCustomEvent.CLEAR_ALL_SERVICE_CACHE, () => {
      cacheStore = {}
    })

    window.addEventListener(AppCustomEvent.CLEAR_SERVICE_CACHE, (event: Event) => {
      const cacheName = (event as CustomEvent<{ cacheName: string }>).detail.cacheName

      delete cacheStore[cacheName]
    })
  }

  return async <T>(
    fn: () => Promise<T>,
    cacheName: string,
    cacheTime: number,
  ): Promise<T> => {
    const lastUpdated = Number(get(cacheStore, `${cacheName}.lastUpdated`, 0))

    const isCacheExpired = Date.now() - lastUpdated >= cacheTime

    if (isCacheExpired) {
      const freshData = await fn()

      set(cacheStore, `${cacheName}.data`, freshData)
      set(cacheStore, `${cacheName}.lastUpdated`, Date.now())

      return cacheStore[cacheName].data as T
    }

    return cacheStore[cacheName].data as T
  }
}

export const invalidateServiceCache = (cacheName: string) => {
  if (typeof window === 'undefined') return
  window.dispatchEvent(
    new CustomEvent(AppCustomEvent.CLEAR_SERVICE_CACHE, {
      detail: {
        cacheName,
      },
    }),
  )
}

export const invalidateAllServiceCache = () => {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(AppCustomEvent.CLEAR_ALL_SERVICE_CACHE))
}

export const serviceCache = createServiceCache()
